import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
  type ReactNode,
} from 'react';
import { getSupabaseClient } from '@/lib/supabase';

// ─── Currency Preference State ───
// This hook is NOT a currency conversion engine.
// Per GBCOSPACK Backend Integration Contract:
//   - No hardcoded exchange rates
//   - No client-side FX conversion for official quotes
//   - Locale is NOT the currency authority
// Official quote conversion is owned by the Backend.
// This hook tracks the user's selected currency preference (hydrated from
// profiles.default_currency and persisted back on change).

interface CurrencyContextValue {
  currency: string;
  setCurrency: (c: string) => void;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: 'USD',
  setCurrency: () => {},
});

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState('USD');
  const userChangedRef = useRef(false);

  // Hydrate from the signed-in user's profile preference (default_currency).
  useEffect(() => {
    let cancelled = false;
    const supabase = getSupabaseClient();
    if (!supabase) return;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user || cancelled) return;
      const { data } = await supabase
        .from('profiles')
        .select('default_currency')
        .eq('id', session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (!userChangedRef.current && data?.default_currency) {
        setCurrencyState(data.default_currency);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const setCurrency = useCallback((c: string) => {
    userChangedRef.current = true;
    setCurrencyState(c);

    // Persist the preference to the profile (best effort).
    const supabase = getSupabaseClient();
    if (supabase) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          supabase
            .from('profiles')
            .update({ default_currency: c })
            .eq('id', session.user.id)
            .then(() => {});
        }
      });
    }
  }, []);

  const value = useMemo(() => ({ currency, setCurrency }), [currency, setCurrency]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  return useContext(CurrencyContext);
}